import { AlertCircle, Check, ChevronDown, Loader2, Users } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useDashboardFilterUsers } from '@/hooks/useDashboardFilterUsers';

export interface DashboardFilterOption {
  id: string;
  name: string;
  initials?: string;
  type: 'all' | 'user' | 'team';
  avatarColor?: string;
}

interface DashboardFilterProps {
  selectedFilter: DashboardFilterOption;
  onFilterChange: (filter: DashboardFilterOption) => void;
}

const EVERYONE_OPTION: DashboardFilterOption = { id: 'everyone', name: 'Everyone', type: 'all' };

export function DashboardFilter({ selectedFilter, onFilterChange }: DashboardFilterProps) {
  const [open, setOpen] = useState(false);
  const { data: users, isLoading, isError } = useDashboardFilterUsers();

  // Build the full options list: "Everyone" + user options from the hook
  const filterOptions: DashboardFilterOption[] = [EVERYONE_OPTION, ...(users ?? [])];

  // Persist filter to localStorage
  useEffect(() => {
    if (selectedFilter.id !== 'everyone') {
      localStorage.setItem('dashboardFilter', JSON.stringify(selectedFilter));
    } else {
      localStorage.removeItem('dashboardFilter');
    }
  }, [selectedFilter]);

  const handleFilterSelect = (filter: DashboardFilterOption) => {
    onFilterChange(filter);
    setOpen(false);
  };

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" className="gap-2">
          {selectedFilter.id === 'everyone' ? (
            <Users className="h-4 w-4" />
          ) : (
            <div
              className="w-6 h-6 rounded-full flex items-center justify-center text-white text-xs font-semibold"
              style={{ backgroundColor: selectedFilter.avatarColor }}
            >
              {selectedFilter.initials}
            </div>
          )}
          <span>{selectedFilter.name}</span>
          <ChevronDown className="h-4 w-4 opacity-50" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        {/* Everyone option is always shown */}
        <DropdownMenuItem onClick={() => handleFilterSelect(EVERYONE_OPTION)} className="cursor-pointer">
          <div className="flex items-center justify-between w-full">
            <div className="flex items-center gap-2">
              <Users className="h-4 w-4" />
              <span>{EVERYONE_OPTION.name}</span>
            </div>
            {selectedFilter.id === 'everyone' && <Check className="h-4 w-4" />}
          </div>
        </DropdownMenuItem>

        <DropdownMenuSeparator />

        {/* Loading state */}
        {isLoading && (
          <div className="flex items-center justify-center gap-2 py-3 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            <span>Loading users...</span>
          </div>
        )}

        {/* Error state */}
        {isError && !isLoading && (
          <div className="flex items-center justify-center gap-2 py-3 text-sm text-muted-foreground">
            <AlertCircle className="h-4 w-4" />
            <span>Failed to load users</span>
          </div>
        )}

        {/* User options */}
        {!isLoading &&
          !isError &&
          filterOptions
            .filter((o) => o.type === 'user')
            .map((option) => (
              <DropdownMenuItem key={option.id} onClick={() => handleFilterSelect(option)} className="cursor-pointer">
                <div className="flex items-center justify-between w-full">
                  <div className="flex items-center gap-2">
                    <div
                      className="w-6 h-6 rounded-full flex items-center justify-center text-white text-xs font-semibold"
                      style={{ backgroundColor: option.avatarColor }}
                    >
                      {option.initials}
                    </div>
                    <span>{option.name}</span>
                  </div>
                  {selectedFilter.id === option.id && <Check className="h-4 w-4" />}
                </div>
              </DropdownMenuItem>
            ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
